import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import Chip from '@mui/joy/Chip';
import Sheet from "@mui/joy/Sheet";
import DebounceInput from "../../../components/debounceInput/DebounceInput";
import Divider from '@mui/joy/Divider';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DesktopAccessDisabledIcon from '@mui/icons-material/DesktopAccessDisabled';
import StoreIcon from '@mui/icons-material/Store';
import PowerIcon from '@mui/icons-material/Power';
import GppBadIcon from '@mui/icons-material/GppBad';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import IconButton, {iconButtonClasses} from "@mui/joy/IconButton";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import CircularProgress from "@mui/joy/CircularProgress";
import Table from "@mui/joy/Table";
import Link from "@mui/joy/Link";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import {NotificationService} from "../../../common/notification";
import {useNavigate} from "react-router-dom";
import Button from "@mui/joy/Button";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {getPages} from "../../../common/pages/pages";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {AdminUserInfo} from "../../../models/user";
import Avatar from "@mui/joy/Avatar";
import { formatDateTime } from "../../../common/dateTime";
import Stack from "@mui/joy/Stack";
import {usersListApi, userRemoveBansApi} from "../usersApi";
import {formatRubles} from "../../../common/money";

export function UserGeneralTable() {
    const navigate = useNavigate();
    const [users, setUsers] = React.useState<AdminUserInfo[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [filterSearch, setFilterSearch] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [filterOnlineState, setFilterOnlineState] = React.useState<number>(-1);
    const [filterTradingState, setFilterTradingState] = React.useState<number>(-1);
    const [filterRoles, setFilterRoles] = React.useState<string[]>([]);
    const [filterOpen, setFilterOpen] = React.useState(false);

    React.useEffect(() => {
        loadData().catch(console.error);
    }, [page, filterSearch, filterOnlineState, filterTradingState, filterRoles])

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await usersListApi(filterSearch, 9, filterOnlineState, filterTradingState, page, 50, filterRoles);
            if (result.result) {
                const res = result.value!;
                setPageCount(Math.ceil(res.total / 50));
                setUsers(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const renderFilters = () => {
        return (
            <React.Fragment>
                <FormControl size="sm">
                    <FormLabel>Статус</FormLabel>
                    <Select
                        size="sm"
                        placeholder="Статус"
                        slotProps={{button: {sx: {whiteSpace: 'nowrap'}}}}
                        value={filterOnlineState}
                        onChange={(_, value) => setFilterOnlineState(value ?? -1)}
                    >
                        <Option value={-1}>Все</Option>
                        <Option value={0}>Офлайн</Option>
                        <Option value={1}>Онлайн</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Торговля</FormLabel>
                    <Select size="sm"
                            placeholder="Прокси"
                            value={filterTradingState}
                            onChange={(_, value) => setFilterTradingState(value ?? -1)}>
                        <Option value={-1}>Все</Option>
                        <Option value={0}>Выключена</Option>
                        <Option value={1}>Активна</Option>                        
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Роли</FormLabel>
                    <Select size="sm" multiple placeholder="Все"
                            value={filterRoles}
                            onChange={(_, value) => setFilterRoles(value as string[])}>
                        <Option value="user">User</Option>
                        <Option value="admin">Admin</Option>
                        <Option value="manager">Manager</Option>
                    </Select>
                </FormControl>
            </React.Fragment>
        );
    }

    const applyFilter = async () => {
        setFilterOpen(false)
        if (page !== 1) {
            setPage(1);
        }
    }

    const removeBan = async (id: bigint) => {
        setLoading(true)
        try {
            let result = await userRemoveBansApi(id);
            if (result.result) {
                NotificationService.alertSuccess("Пометка бана успешно сброшена.")
            } else {
                NotificationService.alertError("Не удалось сбросить пометку бана. Пожалуйста попробуйте позже или обратитесь в службу поддержки.")
            }
        } finally {
            setLoading(false)
        }

        await loadData();
    }

    return (
        <React.Fragment>
            <Sheet
                className="SearchAndFilters-mobile"
                sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
            >
                <DebounceInput
                    size="sm"
                    placeholder="Steam ID, ник, имя"
                    startDecorator={<SearchIcon/>}
                    sx={{flexGrow: 1}}
                    debounceTimeout={1000}
                    handleDebounce={(val) => setFilterSearch(val)}
                />
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    onClick={() => setFilterOpen(true)}
                >
                    <FilterAltIcon/>
                </IconButton>
                <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
                    <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                        <ModalClose/>
                        <Typography id="filter-modal" level="h2">
                            Фильтры
                        </Typography>
                        <Divider sx={{my: 2}}/>
                        <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            {renderFilters()}
                            <Button color="primary" onClick={applyFilter}>
                                Применить
                            </Button>
                            <Button color="neutral" onClick={() => setFilterOpen(false)}>
                                Закрыть
                            </Button>
                        </Sheet>
                    </ModalDialog>
                </Modal>
            </Sheet>
            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'none', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Поиск</FormLabel>
                    <DebounceInput
                        size="sm"
                        placeholder="Steam ID, ник, имя"
                        startDecorator={<SearchIcon/>}
                        debounceTimeout={1000}
                        handleDebounce={(val) => setFilterSearch(val)}
                    />
                </FormControl>
                {renderFilters()}
            </Box>
            {loading ?
                <CircularProgress/> :
                <>
                    <Sheet
                        className="OrderTableContainer"
                        variant="outlined"
                        sx={{
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            minHeight: 0,
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                display: {xs: 'none', sm: 'table'},
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px',
                            }}
                        >
                            <thead>
                            <tr>
                                <th style={{width: 50, padding: '12px 6px'}}></th>
                                <th style={{width: 140, padding: '12px 6px'}}>Steam Id</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Имя пользователя</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Роль</th>
                                <th style={{width: 70, padding: '12px 6px'}}>Баланс</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Ник</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Дата рег.</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Дата акт.</th>
                                <th style={{width: 50, padding: '12px 6px'}}>Статус</th>
                                <th style={{width: 40, padding: '12px 6px'}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {users.map((row) => {
                                return (
                                    <tr key={row.id.toString()} style={row.hasBan ? {backgroundColor: 'red'} : {}}>
                                        <td>
                                            <Avatar
                                                style={row.isOnline ? {borderColor: 'green', borderWidth: 3} : {}}
                                                variant="outlined"
                                                size="sm"
                                                src={row.image}
                                            />
                                        </td>
                                        <td>
                                            <Link
                                                underline="hover"
                                                href={`https://steamcommunity.com/profiles/${row.id}/`}
                                                target="_blank"
                                                level="body-xs"
                                            >
                                                {row.id.toString()}
                                            </Link>
                                        </td>
                                        <td>
                                            <Link
                                                onClick={() => navigate(row.id.toString())}
                                                underline="hover"
                                                level="body-xs"
                                            >
                                                {row.userName}
                                            </Link>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{row.role}</Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                level="body-xs">{formatRubles(row.rub)}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{row.name ?? '-'}</Typography>
                                        </td>
                                        <td>
                                        <Typography
                                                level="body-xs">{formatDateTime(row.created)}</Typography>
                                        </td>
                                        <td>
                                        <Typography
                                                level="body-xs">{formatDateTime(row.lastActivityAt)}</Typography>
                                        </td>
                                        <td>
                                            <Stack
                                                direction="row"
                                                spacing='1'
                                                sx={{
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {row.isOnline ?
                                                    <Tooltip title='В сети'>
                                                        <Chip
                                                            variant="soft"
                                                            size="sm"
                                                            startDecorator={<PowerIcon/>}
                                                            color='success'
                                                        />
                                                    </Tooltip>
                                                    :<Tooltip title='Не в сети'>
                                                        <Chip
                                                            variant="soft"
                                                            size="sm"
                                                            startDecorator={<DesktopAccessDisabledIcon/>}
                                                            color='danger'
                                                        />
                                                    </Tooltip>
                                                }
                                                {row.tradingEnabled ?
                                                    <Tooltip title='Торговля активна'>
                                                        <Chip
                                                            variant="soft"
                                                            size="sm"
                                                            startDecorator={<StoreIcon/>}
                                                            color='success'
                                                        />
                                                    </Tooltip>
                                                    :<></>
                                                }
                                                {row.isAccessBlacklisted ?
                                                    <Tooltip title='Доступ заблокирован'>
                                                        <Chip
                                                            variant="solid"
                                                            size="sm"
                                                            startDecorator={<GppBadIcon/>}
                                                            color='danger'
                                                        />
                                                    </Tooltip>
                                                    :<></>
                                                }
                                            </Stack>
                                        </td>
                                        <td>
                                            {!row.hasBan
                                            ?<></>
                                            :<Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                                                <Dropdown>
                                                    <MenuButton
                                                        slots={{root: IconButton}}
                                                        slotProps={{
                                                            root: {
                                                                variant: 'plain',
                                                                color: 'neutral',
                                                                size: 'sm'
                                                            }
                                                        }}
                                                    >
                                                        <MoreHorizRoundedIcon/>
                                                    </MenuButton>
                                                    <Menu size="sm" sx={{minWidth: 140}}>
                                                        <MenuItem color="warning" onClick={() => removeBan(row.id)}>
                                                            <ListItemIcon sx={{ minWidth: 0 }}><CleaningServicesIcon fontSize="small" /></ListItemIcon>
                                                            <ListItemText primary="Снять пометку бана"
                                                                          primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                        </MenuItem>
                                                    </Menu>
                                                </Dropdown>
                                            </Box>}
                                        </td>
                                    </tr>)
                            })}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box
                        className="Pagination-laptopUp"
                        sx={{
                            pt: 2,
                            gap: 1,
                            [`& .${iconButtonClasses.root}`]: {borderRadius: '50%'},
                            display: {
                                xs: 'none',
                                md: 'flex',
                            },
                        }}
                    >
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            startDecorator={<KeyboardArrowLeftIcon/>}
                            disabled={page === 1}
                            onClick={() => setPage(state => state - 1)}
                        >
                            Назад
                        </Button>

                        <Box sx={{flex: 1}}/>
                        {getPages(pageCount).map((p) => (
                            <IconButton
                                key={p}
                                size="sm"
                                variant={Number(p) ? 'outlined' : 'plain'}
                                color={page === Number(p) ? "primary" : "neutral"}
                                onClick={() => setPage(Number(p))}
                            >
                                {p}
                            </IconButton>
                        ))}
                        <Box sx={{flex: 1}}/>
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            endDecorator={<KeyboardArrowRightIcon/>}
                            disabled={page === pageCount}
                            onClick={() => setPage(state => state + 1)}
                        >
                            Дальше
                        </Button>
                    </Box>

                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {users.map((listItem) => {
                            return (
                                <List key={listItem.id.toString()} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                    <ListItem
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                        }}
                                        style={listItem.hasBan ? {backgroundColor: 'red'} : {}}
                                    >
                                        <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                            <Stack spacing={1} direction={'row'}>
                                                <Avatar
                                                    style={listItem.isOnline ? {
                                                        borderColor: 'green',
                                                        borderWidth: 3
                                                    } : {}}
                                                    variant="outlined"
                                                    size="sm"
                                                    src={listItem.image}
                                                />

                                                <div>
                                                    <Stack>
                                                        <Link
                                                            underline="hover"
                                                            href={`https://steamcommunity.com/profiles/${listItem.id}/`}
                                                            target="_blank"
                                                            level="body-xs"
                                                        >
                                                            {listItem.id.toString()}
                                                        </Link>
                                                        <Link
                                                            onClick={() => navigate(listItem.id.toString())}
                                                            underline="hover"
                                                            level="body-xs"
                                                        >
                                                            Имя пользователя:{listItem.userName}
                                                        </Link>
                                                    </Stack>
                                                    <Typography level="body-xs">Роль: {listItem.role}</Typography>
                                                    <Typography
                                                        level="body-xs">Баланс: {formatRubles(listItem.rub)}</Typography>
                                                    <Typography level="body-xs">Ник: {listItem.name ?? '-'}</Typography>
                                                    <Typography level="body-xs">Дата
                                                        рег.: {formatDateTime(listItem.created)}</Typography>
                                                    <Typography level="body-xs">Дата
                                                        акт.: {formatDateTime(listItem.lastActivityAt)}</Typography>
                                                        {!listItem.hasBan
                                                        ?<></>
                                                        :<Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                                            <Dropdown>
                                                                <MenuButton
                                                                    slots={{root: IconButton}}
                                                                    slotProps={{
                                                                        root: {
                                                                            variant: 'plain',
                                                                            color: 'neutral',
                                                                            size: 'sm'
                                                                        }
                                                                    }}
                                                                >
                                                                    <MoreHorizRoundedIcon/>
                                                                </MenuButton>
                                                                <Menu size="sm" sx={{minWidth: 140}}>
                                                                    <MenuItem color="warning" onClick={() => removeBan(listItem.id)}>
                                                                        <ListItemIcon sx={{ minWidth: 0 }}><CleaningServicesIcon fontSize="small" /></ListItemIcon>
                                                                        <ListItemText primary="Снять пометку бана"
                                                                                    primaryTypographyProps={{ fontSize: 'small', fontWeight: 'medium' }}/>
                                                                    </MenuItem>
                                                                </Menu>
                                                            </Dropdown>
                                                        </Box>}
                                                </div>
                                            </Stack>
                                        </ListItemContent>
                                        {listItem.isOnline ?
                                            <Tooltip title='В сети'>
                                                <Chip
                                                    variant="soft"
                                                    size="sm"
                                                    startDecorator={<PowerIcon/>}
                                                    color='success'
                                                />
                                            </Tooltip>
                                            :<Tooltip title='Не в сети'>
                                                <Chip
                                                    variant="soft"
                                                    size="sm"
                                                    startDecorator={<DesktopAccessDisabledIcon/>}
                                                    color='danger'
                                                />
                                            </Tooltip>
                                        }
                                        {listItem.tradingEnabled ?
                                            <Tooltip title='Торговля активна'>
                                                <Chip
                                                    variant="soft"
                                                    size="sm"
                                                    startDecorator={<StoreIcon/>}
                                                    color='success'
                                                />
                                            </Tooltip>
                                            :<></>
                                        }
                                        {listItem.isAccessBlacklisted ?
                                            <Tooltip title='Доступ заблокирован'>
                                                <Chip
                                                    variant="solid"
                                                    size="sm"
                                                    startDecorator={<GppBadIcon/>}
                                                    color='danger'
                                                />
                                            </Tooltip>
                                            :<></>
                                        }
                                    </ListItem>
                                    <ListDivider/>
                                </List>)
                        })}
                        <Box
                            className="Pagination-mobile"
                            sx={{display: {xs: 'flex', md: 'none'}, alignItems: 'center', py: 2}}
                        >
                            <IconButton
                                aria-label="previous page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => setPage(state => state - 1)}
                            >
                                <KeyboardArrowLeftIcon/>
                            </IconButton>
                            <Typography level="body-sm" sx={{mx: 'auto'}}>
                                Страница {page} из {pageCount}
                            </Typography>
                            <IconButton
                                aria-label="next page"
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                disabled={page === pageCount}
                                onClick={() => setPage(state => state + 1)}
                            >
                                <KeyboardArrowRightIcon/>
                            </IconButton>
                        </Box>
                    </Box>
                </>
            }
        </React.Fragment>
    );
}
